import {
  describeToothBasisSeries,
  renderToothBasisSeries,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type {
  ToothBasisSeriesCacheEntry,
  ToothSliceGeometry,
  ToothSliceOptionsBySeries,
} from '../config';
import {
  MAX_TOOTH_BASIS_SERIES_ABORTS,
  TOOTH_BASIS_SERIES_ORDER,
  TOOTH_SERIES_ABANDON_GRACE_MS,
} from '../config';

import { makeToothBasisSeriesProjectionKeys } from './makeToothBasisSeriesCacheKey';
import { makeToothBasisSeriesRequest } from './makeToothBasisSeriesRequest';
import { makeToothBasisSeriesView } from './makeToothBasisSeriesView';
import {
  getToothBasisSeriesEntry,
  getReusableToothBasisSeries,
  publishToothBasisSeriesEntry,
} from './toothBasisSeriesEntryStore';
import { markToothSliceCardSettled } from './toothSliceCardRegistry';
import { isToothSliceCardVisible } from './toothSliceVisibilityStore';

type ToothBasisSeriesTask = {
  abortCount: number;
  cacheKey: string;
  input: ToothSliceGeometry;
  toothID: string;
  sliceOptions?: ToothSliceOptionsBySeries;
  spacingOutMm?: number;
  reject: (error: unknown) => void;
  resolve: (entry: ToothBasisSeriesCacheEntry) => void;
};

const inFlight = new Map<string, Promise<ToothBasisSeriesCacheEntry>>();
const abandonTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const queueListeners = new Set<() => void>();
const consumersByCacheKey = new Map<string, Set<string>>();

const pendingTasks: ToothBasisSeriesTask[] = [];
const workerState: {
  abortController: AbortController | null;
  generation: number;
  isRunning: boolean;
  priorityToothID: string;
  runningTask: ToothBasisSeriesTask | null;
} = {
  abortController: null,
  generation: 0,
  isRunning: false,
  priorityToothID: '',
  runningTask: null,
};

const notifyQueueListeners = () =>
  queueListeners.forEach((listener) => listener());

export const cancelToothBasisSeriesTasks = () => {
  workerState.generation += 1;
  consumersByCacheKey.clear();
  abandonTimeouts.forEach((timeout) => clearTimeout(timeout));
  abandonTimeouts.clear();
  workerState.abortController?.abort();
  pendingTasks
    .splice(0)
    .forEach((task) =>
      discardTask(task, new DOMException('MPR session ended', 'AbortError')),
    );
  workerState.priorityToothID = '';
  notifyQueueListeners();
};

const rankTask = (task: ToothBasisSeriesTask) => {
  const isVisible = isToothSliceCardVisible(task.toothID);
  const isSelected = task.toothID === workerState.priorityToothID;

  if (isVisible && isSelected) {
    return 0;
  }

  if (isVisible) {
    return 1;
  }

  if (isSelected) {
    return 2;
  }

  return 3;
};

export const getPendingToothSliceCardCount = () =>
  pendingTasks.length + (workerState.runningTask ? 1 : 0);

export const subscribeToToothSliceQueue = (listener: () => void) => {
  queueListeners.add(listener);

  return () => queueListeners.delete(listener);
};

const takeNextTask = (): ToothBasisSeriesTask => {
  const nextIndex = pendingTasks.reduce(
    (best, task, index) =>
      rankTask(task) < rankTask(pendingTasks[best]) ? index : best,
    0,
  );

  return pendingTasks.splice(nextIndex, 1)[0];
};

const abortRunningTaskForPriority = () => {
  const { abortController, priorityToothID, runningTask } = workerState;

  if (!priorityToothID || !runningTask || !abortController) {
    return;
  }

  if (
    abortController.signal.aborted ||
    runningTask.abortCount >= MAX_TOOTH_BASIS_SERIES_ABORTS ||
    runningTask.toothID === priorityToothID ||
    !pendingTasks.some((task) => task.toothID === priorityToothID)
  ) {
    return;
  }

  abortController.abort();
};

const isCacheKeyWanted = (cacheKey: string) =>
  (consumersByCacheKey.get(cacheKey)?.size ?? 0) > 0;

const discardTask = (task: ToothBasisSeriesTask, error: unknown) => {
  inFlight.delete(task.cacheKey);
  markToothSliceCardSettled(task.cacheKey);
  task.reject(error);
};

const abandonCacheKey = (cacheKey: string) => {
  const pendingIndex = pendingTasks.findIndex(
    (task) => task.cacheKey === cacheKey,
  );

  if (pendingIndex >= 0) {
    const [task] = pendingTasks.splice(pendingIndex, 1);

    discardTask(task, new Error('Tooth-basis series request was superseded'));
    notifyQueueListeners();

    return;
  }

  if (workerState.runningTask?.cacheKey === cacheKey) {
    workerState.abortController?.abort();
  }
};

const cancelScheduledAbandon = (cacheKey: string) => {
  const timeout = abandonTimeouts.get(cacheKey);

  if (timeout === undefined) {
    return;
  }

  clearTimeout(timeout);
  abandonTimeouts.delete(cacheKey);
};

const scheduleAbandon = (cacheKey: string) => {
  if (abandonTimeouts.has(cacheKey)) {
    return;
  }

  abandonTimeouts.set(
    cacheKey,
    setTimeout(() => {
      abandonTimeouts.delete(cacheKey);

      if (isCacheKeyWanted(cacheKey)) {
        return;
      }

      consumersByCacheKey.delete(cacheKey);
      abandonCacheKey(cacheKey);
    }, TOOTH_SERIES_ABANDON_GRACE_MS),
  );
};

export const releaseToothBasisSeriesEntry = ({
  cacheKey,
  consumerID,
}: {
  cacheKey: string;
  consumerID: string;
}) => {
  const consumers = consumersByCacheKey.get(cacheKey);

  if (!consumers) {
    return;
  }

  consumers.delete(consumerID);

  if (consumers.size > 0) {
    return;
  }

  scheduleAbandon(cacheKey);
};

const settleTask = ({
  entry,
  task,
}: {
  entry: ToothBasisSeriesCacheEntry;
  task: ToothBasisSeriesTask;
}) => {
  inFlight.delete(task.cacheKey);
  task.resolve(entry);
};

const runTask = async (task: ToothBasisSeriesTask) => {
  const cached = getToothBasisSeriesEntry(task.cacheKey);

  if (cached) {
    settleTask({ entry: cached, task });
    return;
  }

  const abortController = new AbortController();
  const generation = workerState.generation;

  workerState.abortController = abortController;
  workerState.runningTask = task;

  const request = makeToothBasisSeriesRequest({
    input: task.input,
    sliceOptions: task.sliceOptions,
    spacingOutMm: task.spacingOutMm,
  });
  const projectionKeys = makeToothBasisSeriesProjectionKeys(task);
  const reusable = getReusableToothBasisSeries(projectionKeys);
  const { axial, frontal, sagittal } = reusable;
  const missingSeries = TOOTH_BASIS_SERIES_ORDER.filter(
    (seriesName) => !reusable[seriesName],
  );

  try {
    if (axial && frontal && sagittal) {
      const entry: ToothBasisSeriesCacheEntry = {
        ...axial,
        view: {
          axial: axial.view.axial,
          frontal: frontal.view.frontal,
          sagittal: sagittal.view.sagittal,
        },
        slabThicknessMm: {
          axial: axial.slabThicknessMm.axial,
          frontal: frontal.slabThicknessMm.frontal,
          sagittal: sagittal.slabThicknessMm.sagittal,
        },
        sliceStepMm: {
          axial: axial.sliceStepMm.axial,
          frontal: frontal.sliceStepMm.frontal,
          sagittal: sagittal.sliceStepMm.sagittal,
        },
      };
      publishToothBasisSeriesEntry({
        cacheKey: task.cacheKey,
        entry,
        projectionKeys,
      });
      settleTask({ entry, task });
      return;
    }

    const outline =
      missingSeries.length < TOOTH_BASIS_SERIES_ORDER.length
        ? await describeToothBasisSeries(request, abortController.signal)
        : null;
    abortController.signal.throwIfAborted();
    const result = await renderToothBasisSeries(
      request,
      abortController.signal,
      undefined,
      outline
        ? {
            planes: outline.series.flatMap((series) =>
              missingSeries.includes(series.name)
                ? series.offsetsMm.map((offsetMm) => ({
                    offsetMm,
                    seriesName: series.name,
                  }))
                : [],
            ),
          }
        : undefined,
    );

    if (generation !== workerState.generation) {
      discardTask(task, new DOMException('MPR session ended', 'AbortError'));
      return;
    }
    const renderedView = makeToothBasisSeriesView(result.series);
    const view = outline
      ? (Object.fromEntries(
          TOOTH_BASIS_SERIES_ORDER.map((name) => [
            name,
            reusable[name]?.view[name] ?? renderedView[name],
          ]),
        ) as ToothBasisSeriesCacheEntry['view'])
      : renderedView;

    const entry: ToothBasisSeriesCacheEntry = {
      frames: result.frames ?? null,
      massCentroidMm: result.massCentroidMm ?? null,
      profiles: result.profiles ?? null,
      slabThicknessMm: result.slabThicknessMm,
      sliceStepMm: result.sliceStepMm,
      view,
    };

    publishToothBasisSeriesEntry({
      cacheKey: task.cacheKey,
      entry,
      projectionKeys,
    });
    settleTask({ entry, task });
  } catch (error) {
    if (generation !== workerState.generation) {
      discardTask(task, error);
      return;
    }
    if (abortController.signal.aborted) {
      if (!isCacheKeyWanted(task.cacheKey)) {
        discardTask(task, error);

        return;
      }

      pendingTasks.unshift({ ...task, abortCount: task.abortCount + 1 });

      return;
    }

    discardTask(task, error);
  } finally {
    workerState.abortController = null;
    workerState.runningTask = null;
  }
};

const runQueue = async () => {
  if (workerState.isRunning) {
    return;
  }

  workerState.isRunning = true;

  while (pendingTasks.length > 0) {
    await runTask(takeNextTask());
    notifyQueueListeners();
  }

  workerState.isRunning = false;
  notifyQueueListeners();
};

export const prioritizeToothBasisSeries = (toothID: string) => {
  workerState.priorityToothID = toothID;
  abortRunningTaskForPriority();
};

export const getCachedToothBasisSeriesEntry = (cacheKey: string) =>
  getToothBasisSeriesEntry(cacheKey);

export const loadToothBasisSeriesEntry = ({
  cacheKey,
  consumerID,
  input,
  sliceOptions,
  spacingOutMm,
}: {
  cacheKey: string;
  consumerID: string;
  input: ToothSliceGeometry;
  sliceOptions?: ToothSliceOptionsBySeries;
  spacingOutMm?: number;
}): Promise<ToothBasisSeriesCacheEntry> => {
  const consumers = consumersByCacheKey.get(cacheKey) ?? new Set<string>();

  consumers.add(consumerID);
  consumersByCacheKey.set(cacheKey, consumers);
  cancelScheduledAbandon(cacheKey);

  const cached = getToothBasisSeriesEntry(cacheKey);

  if (cached) {
    return Promise.resolve(cached);
  }

  const pending = inFlight.get(cacheKey);

  if (pending) {
    return pending;
  }

  const task = new Promise<ToothBasisSeriesCacheEntry>((resolve, reject) => {
    queueMicrotask(notifyQueueListeners);
    pendingTasks.push({
      abortCount: 0,
      cacheKey,
      input,
      reject,
      resolve,
      sliceOptions,
      spacingOutMm,
      toothID: input.toothID,
    });
  });

  inFlight.set(cacheKey, task);
  abortRunningTaskForPriority();
  void runQueue();

  return task;
};
